import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { scrapeBusinessData, saveScrapedLeads, loadScrapedLeads, updateScrapedLeadStatus, exportScrapedLeadsCSV } from '../../../services/webScraperService';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ui/Toast';

const WebScraperPanel = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [maxResults, setMaxResults] = useState('10');
  const [isScrapingActive, setIsScrapingActive] = useState(false);
  const [scrapedResults, setScrapedResults] = useState([]);
  const [savedLeads, setSavedLeads] = useState([]);
  const [activeView, setActiveView] = useState('scraper'); // 'scraper' | 'saved'
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);
  const [error, setError] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [scrapeProgress, setScrapeProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const industryOptions = [
    { value: '', label: 'All Industries' },
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'marketing', label: 'Marketing & Advertising' },
    { value: 'construction', label: 'Construction' },
    { value: 'education', label: 'Education' },
    { value: 'legal', label: 'Legal Services' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'food-beverage', label: 'Food & Beverage' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'beauty', label: 'Beauty & Wellness' },
    { value: 'fitness', label: 'Fitness & Sports' }
  ];

  const companySizeOptions = [
    { value: '', label: 'Any Size' },
    { value: 'startup', label: 'Startup (1-10)' },
    { value: 'small', label: 'Small (11-50)' },
    { value: 'medium', label: 'Medium (51-200)' },
    { value: 'large', label: 'Large (201-1000)' },
    { value: 'enterprise', label: 'Enterprise (1000+)' }
  ];

  const maxResultsOptions = [
    { value: '5', label: '5 results' },
    { value: '10', label: '10 results' },
    { value: '15', label: '15 results' },
    { value: '20', label: '20 results' }
  ];

  useEffect(() => {
    if (user?.id) {
      fetchSavedLeads();
    }
  }, [user?.id]);

  const fetchSavedLeads = async () => {
    try {
      const leads = await loadScrapedLeads(user?.id);
      setSavedLeads(leads || []);
    } catch (err) {
      console.warn('Could not load saved leads:', err?.message);
    }
  };

  const handleScrape = async () => {
    if (!query?.trim()) {
      setError('Please enter a search query to scrape leads.');
      toast?.warning('Please enter a search query to scrape leads.', 'Query Required');
      return;
    }
    if (!user?.id) {
      toast?.warning('Please sign in to use the web scraper.', 'Authentication Required');
      return;
    }
    setError(null);
    setIsScrapingActive(true);
    setScrapedResults([]);
    setScrapeProgress(0);
    setSelectedLeads([]);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setScrapeProgress(prev => {
        if (prev >= 85) { clearInterval(progressInterval); return prev; }
        return prev + Math.floor(Math.random() * 15) + 5;
      });
    }, 600);

    try {
      const results = await scrapeBusinessData(query, {
        industry,
        location,
        companySize,
        maxResults: parseInt(maxResults)
      });
      clearInterval(progressInterval);
      setScrapeProgress(100);
      setScrapedResults(results || []);
      toast?.success(`Found ${results?.length || 0} businesses matching your search.`, 'Scraping Complete');
    } catch (err) {
      clearInterval(progressInterval);
      const msg = 'Scraping failed: ' + (err?.message || 'Unknown error');
      setError(msg);
      toast?.error(msg, 'Scraping Failed');
    } finally {
      setIsScrapingActive(false);
      setTimeout(() => setScrapeProgress(0), 1000);
    }
  };

  const handleSaveSelected = async () => {
    if (!selectedLeads?.length) {
      setError('Please select at least one lead to save.');
      toast?.warning('Please select at least one lead to save.', 'No Selection');
      return;
    }
    setIsSaving(true);
    setError(null);
    const leadsToSave = scrapedResults?.filter(l => selectedLeads?.includes(l?.id));
    try {
      const result = await saveScrapedLeads(leadsToSave, user?.id);
      const msg = `Saved ${result?.saved} lead${result?.saved !== 1 ? 's' : ''} successfully!`;
      setSaveStatus(msg);
      toast?.success(msg, 'Leads Saved');
      setSelectedLeads([]);
      await fetchSavedLeads();
      setTimeout(() => setSaveStatus(null), 4000);
    } catch (err) {
      const msg = 'Failed to save leads: ' + (err?.message || 'Unknown error');
      setError(msg);
      toast?.error(msg, 'Save Failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (!scrapedResults?.length) {
      toast?.warning('No results to save. Run a scrape first.', 'Nothing to Save');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const result = await saveScrapedLeads(scrapedResults, user?.id);
      const msg = `Saved ${result?.saved} lead${result?.saved !== 1 ? 's' : ''} successfully!`;
      setSaveStatus(msg);
      toast?.success(msg, 'All Leads Saved');
      setSelectedLeads([]);
      await fetchSavedLeads();
      setTimeout(() => setSaveStatus(null), 4000);
    } catch (err) {
      const msg = 'Failed to save leads: ' + (err?.message || 'Unknown error');
      setError(msg);
      toast?.error(msg, 'Save Failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    const leadsToExport = activeView === 'saved' ? savedLeads : scrapedResults;
    if (!leadsToExport?.length) {
      toast?.warning('No leads to export.', 'Nothing to Export');
      return;
    }
    exportScrapedLeadsCSV(leadsToExport);
    toast?.success(`Exported ${leadsToExport?.length} leads to CSV.`, 'Export Complete');
  };

  const handleToggleSelect = (leadId) => {
    setSelectedLeads(prev =>
      prev?.includes(leadId) ? prev?.filter(id => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads?.length === scrapedResults?.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(scrapedResults?.map(l => l?.id));
    }
  };

  const handleStatusChange = async (leadId, status) => {
    try {
      await updateScrapedLeadStatus(leadId, status);
      await fetchSavedLeads();
      toast?.success('Lead status updated.', 'Updated');
    } catch (err) {
      toast?.error('Failed to update status.', 'Error');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusBadge = (status) => {
    const map = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      qualified: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return map?.[status] || 'bg-gray-100 text-gray-700';
  };

  const renderSocialLinks = (social) => {
    if (!social) return <span className="text-xs text-muted-foreground">No social profiles</span>;
    const platforms = [
      { key: 'linkedin', label: 'LI', color: 'text-blue-600 bg-blue-50' },
      { key: 'facebook', label: 'FB', color: 'text-blue-500 bg-blue-50' },
      { key: 'instagram', label: 'IG', color: 'text-pink-500 bg-pink-50' },
      { key: 'twitter', label: 'TW', color: 'text-sky-500 bg-sky-50' }
    ];
    const available = platforms?.filter(p => social?.[p?.key]);
    if (!available?.length) return <span className="text-xs text-muted-foreground">No social profiles</span>;
    return (
      <div className="flex items-center space-x-1">
        {available?.map(p => (
          <a
            key={p?.key}
            href={social?.[p?.key]}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs px-1.5 py-0.5 rounded font-medium ${p?.color} hover:opacity-70 transition-opacity`}
          >
            {p?.label}
          </a>
        ))}
      </div>
    );
  };

  const renderLeadCard = (lead, isSaved = false) => {
    const isExpanded = expandedLead === lead?.id;
    const isSelected = selectedLeads?.includes(lead?.id);
    const socialData = isSaved
      ? { linkedin: lead?.linkedin_url, facebook: lead?.facebook_url, instagram: lead?.instagram_url, twitter: lead?.twitter_url }
      : lead?.social_media;
    const contactData = isSaved
      ? { first_name: lead?.first_name, last_name: lead?.last_name, title: lead?.job_title, email: lead?.email, phone: lead?.phone }
      : lead?.contact;
    const locationData = isSaved
      ? { city: lead?.city, state: lead?.state, country: lead?.country }
      : lead?.location;
    const companyName = isSaved ? lead?.company_name : lead?.company_name;
    const score = lead?.lead_score || 0;

    return (
      <div
        key={lead?.id}
        className={`border rounded-lg transition-all duration-200 ${
          isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'
        }`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1 min-w-0">
              {!isSaved && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleSelect(lead?.id)}
                  className="mt-1 w-4 h-4 text-primary rounded border-border cursor-pointer"
                />
              )}
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {(companyName || 'B')?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 flex-wrap gap-1">
                  <h4 className="font-semibold text-foreground text-sm">{companyName || 'Unknown Company'}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getScoreColor(score)}`}>
                    Score: {score}
                  </span>
                  {isSaved && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(lead?.status)}`}>
                      {lead?.status || 'new'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {lead?.industry} {locationData?.city ? `• ${locationData?.city}, ${locationData?.state || ''}` : ''}
                </p>
                {contactData?.email && (
                  <p className="text-xs text-blue-600 mt-0.5">{contactData?.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
              {lead?.website && (
                <a
                  href={lead?.website?.startsWith('http') ? lead?.website : `https://${lead?.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                  onClick={e => e?.stopPropagation()}
                >
                  <Icon name="ExternalLink" size={14} />
                </a>
              )}
              <button
                onClick={() => setExpandedLead(isExpanded ? null : lead?.id)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
              </button>
            </div>
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-border space-y-3">
              {lead?.description && (
                <p className="text-sm text-muted-foreground">{lead?.description}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {contactData?.first_name && (
                  <div>
                    <span className="text-muted-foreground">Contact: </span>
                    <span className="font-medium">{contactData?.first_name} {contactData?.last_name}</span>
                    {contactData?.title && <span className="text-muted-foreground"> • {contactData?.title}</span>}
                  </div>
                )}
                {contactData?.phone && (
                  <div>
                    <span className="text-muted-foreground">Phone: </span>
                    <a href={`tel:${contactData?.phone}`} className="text-primary">{contactData?.phone}</a>
                  </div>
                )}
                {lead?.company_size && (
                  <div>
                    <span className="text-muted-foreground">Size: </span>
                    <span className="font-medium">{lead?.company_size}</span>
                  </div>
                )}
                {lead?.revenue_range && (
                  <div>
                    <span className="text-muted-foreground">Revenue: </span>
                    <span className="font-medium">{lead?.revenue_range}</span>
                  </div>
                )}
                {lead?.founded_year && (
                  <div>
                    <span className="text-muted-foreground">Founded: </span>
                    <span className="font-medium">{lead?.founded_year}</span>
                  </div>
                )}
                {lead?.scrape_source && (
                  <div>
                    <span className="text-muted-foreground">Source: </span>
                    <span className="font-medium">{lead?.scrape_source}</span>
                  </div>
                )}
              </div>
              {lead?.technologies?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Technologies:</p>
                  <div className="flex flex-wrap gap-1">
                    {lead?.technologies?.slice(0, 6)?.map((tech, i) => (
                      <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded">{tech}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Social:</span>
                {renderSocialLinks(socialData)}
              </div>
              {isSaved && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">Status:</span>
                  <select
                    value={lead?.status || 'new'}
                    onChange={e => handleStatusChange(lead?.id, e?.target?.value)}
                    className="text-xs border border-border rounded px-2 py-1 bg-background"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <Icon name="Globe" size={24} />
          <h2 className="text-xl font-bold">AI Web Scraper</h2>
        </div>
        <p className="text-blue-100 text-sm">Extract business lead data from the web using AI-powered analysis. Find companies, contacts, and social profiles automatically.</p>
      </div>

      {/* View Toggle */}
      <div className="flex space-x-2">
        <button
          onClick={() => setActiveView('scraper')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'scraper' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          🔍 Scraper
        </button>
        <button
          onClick={() => setActiveView('saved')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'saved' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          💾 Saved Leads ({savedLeads?.length})
        </button>
      </div>

      {activeView === 'scraper' && (
        <div className="space-y-4">
          {/* Search Form */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-foreground">Search Parameters</h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Search Query *</label>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e?.target?.value)}
                onKeyDown={e => e?.key === 'Enter' && handleScrape()}
                placeholder="e.g. Digital marketing agencies in New York, SaaS companies in Austin..."
                className="w-full border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Industry"
                options={industryOptions}
                value={industry}
                onChange={setIndustry}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={e => setLocation(e?.target?.value)}
                  placeholder="City, State, Country..."
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <Select
                label="Company Size"
                options={companySizeOptions}
                value={companySize}
                onChange={setCompanySize}
              />
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-40">
                <Select
                  label="Max Results"
                  options={maxResultsOptions}
                  value={maxResults}
                  onChange={setMaxResults}
                />
              </div>
              <div className="flex-1 flex items-end">
                <Button
                  variant="default"
                  onClick={handleScrape}
                  loading={isScrapingActive}
                  iconName="Search"
                  iconPosition="left"
                  className="w-full md:w-auto"
                >
                  {isScrapingActive ? 'Scraping...' : 'Start Scraping'}
                </Button>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {isScrapingActive && (
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Scraping in progress...</span>
                <span className="text-sm text-muted-foreground">{scrapeProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-500"
                  style={{ width: `${scrapeProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">AI is analyzing web data and extracting business information...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
              <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Save Status */}
          {saveStatus && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
              <Icon name="CheckCircle" size={18} className="text-green-500 flex-shrink-0" />
              <p className="text-sm text-green-700">{saveStatus}</p>
            </div>
          )}

          {/* Results */}
          {scrapedResults?.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="font-semibold text-foreground">{scrapedResults?.length} Results Found</h3>
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-primary hover:text-primary/80 font-medium"
                  >
                    {selectedLeads?.length === scrapedResults?.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  {selectedLeads?.length > 0 && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleSaveSelected}
                      loading={isSaving}
                      iconName="Save"
                      iconPosition="left"
                    >
                      Save {selectedLeads?.length} Selected
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveAll}
                    loading={isSaving}
                    iconName="Database"
                    iconPosition="left"
                  >
                    Save All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Export CSV
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {scrapedResults?.map(lead => renderLeadCard(lead, false))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isScrapingActive && scrapedResults?.length === 0 && (
            <div className="text-center py-16 bg-card border border-border rounded-xl">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Globe" size={28} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Ready to Scrape</h3>
              <p className="text-muted-foreground max-w-md mx-auto text-sm">
                Enter a search query above to extract business lead data from the web using AI. Try searches like "Digital marketing agencies in New York" or "SaaS startups in Austin".
              </p>
            </div>
          )}
        </div>
      )}

      {activeView === 'saved' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">{savedLeads?.length} Saved Leads</h3>
            {savedLeads?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
              >
                Export CSV
              </Button>
            )}
          </div>
          {savedLeads?.length > 0 ? (
            <div className="space-y-3">
              {savedLeads?.map(lead => renderLeadCard(lead, true))}
            </div>
          ) : (
            <div className="text-center py-16 bg-card border border-border rounded-xl">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Database" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Saved Leads</h3>
              <p className="text-muted-foreground text-sm">Scrape some leads and save them here for later use.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setActiveView('scraper')}
              >
                Go to Scraper
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WebScraperPanel;
